import { appendId } from "../../../web-workers/auction-socket";
import { type CountDownController, globalSiteCounter } from "../../countdown";

import {
    type IProductSpecIterator,
    type IProductSpecItem,
    type MultiUpdaterType,
} from "..";
import { getSpecIterators } from "../spec-iterator";
import { AxiosError, CanceledError } from "axios";

export class AuctionCountdownTimer {
    public timeoutId: any = null;

    private initialized: boolean = false;

    public iterators: IProductSpecIterator[];

    private countdownController: CountDownController;

    constructor() {
        this.iterators = getSpecIterators();
        this.countdownController = globalSiteCounter();

        for (const {} of this.iterateSpecs()) {
            this.initialize();
            break;
        }
    }

    private *iterateSpecs(): Generator<IProductSpecItem, any, any> {
        for (const iterator of this.iterators) {
            for (const item of iterator.getItems()) {
                yield item;
            }
        }
    }

    public initialize() {
        if (this.initialized) return;
        this.initialized = true;
        console.debug("INITIALIZING COUNTDOWN");
        this.countdownController.initialize();
        this.loopFunction();
    }

    private loopFunction() {
        requestAnimationFrame(() => {
            this.timeoutId = setTimeout(() => {
                this.iterate();
                this.loopFunction();
            }, 1000);
        });
    }

    iterate() {
        const self = this;

        const requestProps: any = {
            Timer50ms: [],
            Timer15s: [],
            Timer30s: [],
            Timer60s: [],
        };

        for (const specItem of this.iterateSpecs()) {
            if (specItem.shouldAppendToAuctionSocket) {
                appendId(
                    specItem.auctionSocketId,
                    specItem.boundSetMarkForUpdate
                );
            }

            const secondsLeft = specItem.getSecondsLeft();
            if (
                specItem.forced ||
                (secondsLeft >= 0 &&
                    secondsLeft < 3600 &&
                    !specItem.getRefreshTriggered())
            ) {
                specItem.setRefreshTriggered(true);

                if (specItem.forced) {
                    if (specItem.shouldQueue)
                        requestProps.Timer50ms.push(specItem);
                    else
                        requestAnimationFrame(() => {
                            setTimeout(() => {
                                self.updateProduct(
                                    specItem,
                                    "Timer50ms_Single"
                                );
                            }, 50);
                        });
                } else if (secondsLeft < 70) {
                    if (specItem.shouldQueue)
                        requestProps.Timer15s.push(specItem);
                    else
                        requestAnimationFrame(() => {
                            setTimeout(() => {
                                self.updateProduct(specItem, "Timer15s_Single");
                            }, 15000);
                        });
                } else if (secondsLeft < 600) {
                    if (specItem.shouldQueue)
                        requestProps.Timer30s.push(specItem);
                    else
                        requestAnimationFrame(() => {
                            setTimeout(() => {
                                self.updateProduct(specItem, "Timer30s_Single");
                            }, 30000);
                        });
                } else {
                    if (specItem.shouldQueue)
                        requestProps.Timer60s.push(specItem);
                    else
                        requestAnimationFrame(() => {
                            setTimeout(() => {
                                self.updateProduct(specItem, "Timer60s_Single");
                            }, 60000);
                        });
                }
            }
        }

        if (requestProps.Timer50ms.length > 0)
            requestAnimationFrame(() => {
                setTimeout(() => {
                    this.updateProducts(
                        requestProps.Timer50ms,
                        "Timer50ms_Queued"
                    );
                }, 50);
            });
        if (requestProps.Timer15s.length > 0)
            requestAnimationFrame(() => {
                setTimeout(() => {
                    this.updateProducts(
                        requestProps.Timer15s,
                        "Timer15s_Queued"
                    );
                }, 15000);
            });
        if (requestProps.Timer30s.length > 0)
            requestAnimationFrame(() => {
                setTimeout(() => {
                    this.updateProducts(
                        requestProps.Timer30s,
                        "Timer30s_Queued"
                    );
                }, 30000);
            });
        if (requestProps.Timer60s.length > 0)
            requestAnimationFrame(() => {
                setTimeout(() => {
                    this.updateProducts(
                        requestProps.Timer60s,
                        "Timer60s_Queued"
                    );
                }, 60000);
            });
    }

    async updateProducts(specItems: IProductSpecItem[], context: string) {
        if (specItems.length == 0) return;
        if (specItems.length == 1) {
            this.updateProduct(specItems[0], context);
            return;
        }
        const typeMappers: {
            type: MultiUpdaterType;
            items: IProductSpecItem[];
        }[] = [];
        for (const specItem of specItems) {
            let match = typeMappers.find(
                (tm) => tm.type === specItem.multiUpdaterType
            );
            if (match == null) {
                match = { type: specItem.multiUpdaterType, items: [] };
                typeMappers.push(match);
            }
            match.items.push(specItem);
        }

        const promises = [];
        for (const typeMapper of typeMappers) {
            const multiUpdater = new typeMapper.type();
            const promise = (async (items: IProductSpecItem[]) => {
                try {
                    await multiUpdater.update(items, context);
                } catch (e) {
                    if (e instanceof CanceledError) {
                        return;
                    }
                    throw e;
                }
                for (const item of items) {
                    item.resetMarkForUpdate();
                }
            })(typeMapper.items);
            promises.push(promise);
        }
        await Promise.all(promises);
    }

    updateProduct(specItem: IProductSpecItem, context: string) {
        try {
            specItem.update(context);
        } catch (e) {
            if (e instanceof CanceledError) {
                return;
            }
            throw e;
        }
        specItem.resetMarkForUpdate();
    }
}
